import { Component, inject, input, output, signal, computed, ViewChild, TemplateRef, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RequestService, Request } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { CatalogService } from '@services/data/catalog.service';
import { Global } from '@services/core/Global';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';

export interface StepApprovalPayload {
  gloss: string;
  file?: File;
  documentUuid?: string;
  documentTypeName: string;
  documentTypeSlug?: string;
  requiresDocument: boolean;
  payDetails?: { txNumber: string; payDate: string; target?: 'CAMPUS' | 'SOCIO' };
  approvedDeductible?: number;
  approvedAmount?: number;
  invoicedAmount?: number;
}

@Component({
  selector: 'app-step-approval-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './step-approval-modal.component.html',
  styleUrls: ['./step-approval-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class StepApprovalModalComponent {
  // Modal de aprobacion de paso BPM
  request = input<Request | null>(null);
  isProcessing = input<boolean>(false);

  confirmed = output<StepApprovalPayload>();
  cancelled = output<void>();

  @ViewChild('approvalModal') modalTemplate!: TemplateRef<any>;
  private activeModal: NgbModalRef | null = null;

  private modalService = inject(NgbModal);
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);
  private requestService = inject(RequestService);
  private sanitizer = inject(DomSanitizer);
  private imageOptimizer = inject(ImageOptimizerService);
  private destroyRef = inject(DestroyRef);
  readonly appSettings = inject(AppSettings);

  /** En móvil: pantalla completa con page-header + mobile-footer (no NgbModal). */
  mobileOpen = signal(false);

  gloss = signal('');
  selectedFile = signal<File | null>(null);
  fileName = signal('');
  /** Vista previa imagen (SecurityContext.URL). */
  previewImgUrl = signal<SafeUrl | null>(null);
  /** Vista previa PDF en iframe (SecurityContext.RESOURCE_URL). */
  previewPdfUrl = signal<SafeResourceUrl | null>(null);
  previewKind = signal<'image' | 'pdf' | 'none'>('none');
  reusedDoc = signal<any | null>(null);
  isLookingUpDoc = signal(false);
  isOptimizingFile = signal(false);

  payDate = signal(new Date().toISOString().split('T')[0]);
  payTarget = signal<'CAMPUS' | 'SOCIO'>('CAMPUS');
  approvedDeductible = signal<number | null>(null);
  approvedAmount = signal<number | null>(null);
  invoicedAmount = signal<number | null>(null);

  isCommissionStep = computed(() => this.bpmService.isCommissionApprovalStep(this.request()));
  isAwaitingInvoiceStep = computed(() => this.bpmService.isAwaitingInvoiceStep(this.request()));

  effectiveDeductiblePct = computed(() => {
    const claim = this.request()?.claim;
    return Number(claim?.approvedDeductible ?? claim?.deductible ?? 0);
  });

  calculatedInvoiceDeductible = computed(() => {
    const inv = Number(this.invoicedAmount() ?? 0);
    return inv > 0 ? Math.round(inv * (this.effectiveDeductiblePct() / 100)) : 0;
  });

  calculatedInvoiceNet = computed(() => {
    const inv = Number(this.invoicedAmount() ?? 0);
    return inv > 0 ? Math.max(0, inv - this.calculatedInvoiceDeductible()) : 0;
  });

  approvedCap = computed(() => {
    const claim = this.request()?.claim;
    return Number(claim?.approvedAmount ?? this.request()?.amount ?? 0);
  });

  finalReimbursementAmount = computed(() => {
    const net = this.calculatedInvoiceNet();
    const cap = this.approvedCap();
    return cap > 0 ? Math.min(net, cap) : net;
  });

  isCapApplied = computed(() => {
    const cap = this.approvedCap();
    return cap > 0 && this.calculatedInvoiceNet() > cap;
  });

  private lookupTimer: ReturnType<typeof setTimeout> | null = null;
  private localBlobUrl: string | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.revokeLocalBlob());
  }

  // Detecta si el paso actual corresponde a pago o reembolso según BPM
  isPaymentStep = computed(() => this.bpmService.isPaymentStep(this.request()));
  isReimbursement = computed(() => this.bpmService.nextStatusSlug(this.request()) === 'REIMBURSED');
  /** Subsanar observación BPM: exige comprobante nuevo. */
  isResubmitObserve = computed(() =>
    String(this.request()?.statusSlug || '').toUpperCase() === 'OBSERVED'
  );

  // Determina si el paso BPM exige adjuntar documento o pago
  requiresDocument = computed(() => {
    if (this.isResubmitObserve()) return true;
    return this.bpmService.stepRequiresDocument(this.request()) || this.isPaymentStep();
  });

  // Slug del tipo de documento configurado en status-flow (o comprobante de pago)
  stepDocSlug = computed(() => {
    if (this.isResubmitObserve() || this.isPaymentStep()) return 'COMPROBANTE_PAGO';
    return this.bpmService.stepDocumentTypeSlug(this.request()) ?? '';
  });

  // Nombre legible del documento del paso (viene del catálogo / BPM)
  documentTypeName = computed(() => {
    if (this.isReimbursement()) return 'Comprobante de Reembolso';
    if (this.isResubmitObserve() || this.isPaymentStep()) return 'Comprobante de Pago';
    return this.bpmService.stepDocumentTypeName(this.request(), 'Documento');
  });

  // Documento ya persistido en la BD para la solicitud
  existingDocInDb = computed(() => {
    if (this.isResubmitObserve()) return null; // no reutilizar el observado
    const docs = this.request()?.claim?.documents ?? [];
    const target = this.stepDocSlug();
    const list = docs.filter((d: any) => (d.typeSlug ?? d.type?.slug) === target);
    return list.length ? list[list.length - 1] : null;
  });

  hasDocInDb = computed(() => !!this.existingDocInDb());

  effectiveGloss = computed(() => this.gloss().trim());

  needsFileUpload = computed(() => {
    if (this.isResubmitObserve()) return !this.selectedFile();
    return this.requiresDocument() && !this.hasDocInDb() && !this.reusedDoc() && !this.selectedFile();
  });

  modalTitle = computed(() => {
    if (this.isResubmitObserve()) return 'Subsanar observación';
    if (this.isReimbursement()) return 'Registrar Reembolso';
    if (this.isPaymentStep()) return 'Registrar Pago';
    if (this.isCommissionStep()) return 'Aprobación de Comisión';
    if (this.isAwaitingInvoiceStep()) return 'Registrar Factura de Reparación';
    return `Aprobar: ${this.documentTypeName()}`;
  });

  glossLabel = computed(() => {
    if (this.isResubmitObserve() || this.isPaymentStep()) return 'Nº de Transacción / Comprobante';
    return `Glosa / Nº de ${this.documentTypeName()}`;
  });

  glossPlaceholder = computed(() => {
    return (this.isResubmitObserve() || this.isPaymentStep()) ? 'Ej: 987654321' : 'Ej: 001-2026';
  });

  fileSectionLabel = computed(() => {
    if (this.isResubmitObserve()) return 'Adjuntar nuevo comprobante';
    return this.needsFileUpload() ? `Adjuntar ${this.documentTypeName()}` : `${this.documentTypeName()} adjunto`;
  });

  confirmBtnLabel = computed(() => {
    if (this.isResubmitObserve()) return 'Reenviar a revisión';
    if (this.isReimbursement()) return 'Confirmar Reembolso';
    if (this.isPaymentStep()) return 'Confirmar Pago';
    if (this.isCommissionStep()) return 'Confirmar Aprobación Comisión';
    if (this.isAwaitingInvoiceStep()) return 'Confirmar Factura y Avanzar';
    return 'Confirmar Aprobación';
  });

  blockedReason = computed(() => {
    if (this.isCommissionStep() && !(Number(this.approvedAmount()) > 0)) {
      return 'Debe indicar un monto tope aprobado válido.';
    }
    if (this.isAwaitingInvoiceStep() && !(Number(this.invoicedAmount()) > 0)) {
      return 'Debe ingresar el monto total de la factura o boleta.';
    }
    if (this.requiresDocument() && !this.effectiveGloss()) {
      return `Debe indicar el número o glosa del ${this.documentTypeName()}.`;
    }
    if (this.needsFileUpload()) {
      return this.isResubmitObserve()
        ? 'Adjunte el nuevo comprobante (imagen o PDF) para reenviar.'
        : `Adjunte el archivo del ${this.documentTypeName()} (imagen o PDF).`;
    }
    return '';
  });

  open(): void {
    this.initForm();
    if (this.appSettings.isMobile()) {
      this.mobileOpen.set(true);
      return;
    }
    this.activeModal = this.modalService.open(this.modalTemplate, {
      backdrop: 'static',
      size: 'lg',
      centered: true,
      scrollable: true
    });
  }

  close(): void {
    if (this.isProcessing()) return;
    this.forceClose();
    this.cancelled.emit();
  }

  /** Cierra el modal aunque esté en procesamiento (p. ej. tras confirmar con éxito). */
  forceClose(): void {
    this.mobileOpen.set(false);
    this.activeModal?.dismiss();
    this.activeModal = null;
  }

  private initForm(): void {
    const req = this.request();
    const initialGloss = this.bpmService.documentGloss(req);
    this.gloss.set(initialGloss);
    this.payDate.set(new Date().toISOString().split('T')[0]);
    this.payTarget.set('CAMPUS');
    this.clearFile();
    this.loadInitialPreview();
    this.initClaimFields(req);
  }

  private initClaimFields(req: Request | null): void {
    const claim = req?.claim;
    if (this.isCommissionStep()) {
      this.seedCommissionDefaults(claim);
    } else if (this.isAwaitingInvoiceStep()) {
      this.invoicedAmount.set(claim?.invoicedAmount ? Number(claim.invoicedAmount) : null);
    }
  }

  private seedCommissionDefaults(claim: any): void {
    const details = Array.isArray(claim?.details) ? claim.details : [];
    const gross = details.reduce((sum: number, d: any) => sum + Number(d.totalCost ?? 0), 0) || Number(claim?.totalCost ?? 0);
    const ded = details.reduce((sum: number, d: any) => sum + Number(d.deductible ?? 0), 0) || Number(claim?.deductible ?? 0);
    const net = Math.max(0, gross - ded);
    const pct = gross > 0 ? Math.round((ded / gross) * 100) : 0;
    this.approvedDeductible.set(Number(claim?.approvedDeductible ?? pct));
    this.approvedAmount.set(Number(claim?.approvedAmount ?? net));
  }

  onApprovedDeductibleInput(event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    this.approvedDeductible.set(Number.isFinite(val) ? val : 0);
  }

  onApprovedAmountInput(event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    this.approvedAmount.set(Number.isFinite(val) ? val : 0);
  }

  onInvoicedAmountInput(event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    this.invoicedAmount.set(Number.isFinite(val) && val > 0 ? val : null);
  }

  private loadInitialPreview(): void {
    const doc = this.existingDocInDb();
    const file = doc?.document ?? {};
    const uuid = file.uuid ?? file.fileUuid ?? null;
    if (uuid) {
      this.setPreviewFromUuid(uuid, doc.fileName ?? file.fileName);
    }
  }

  setPayTarget(target: 'CAMPUS' | 'SOCIO'): void {
    this.payTarget.set(target);
  }

  onPayDateInput(event: Event): void {
    this.payDate.set((event.target as HTMLInputElement).value);
  }

  onGlossInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.gloss.set(value);
    this.scheduleGlossLookup(value.trim());
  }

  private scheduleGlossLookup(query: string): void {
    if (this.lookupTimer) clearTimeout(this.lookupTimer);
    if (!query || this.isPaymentStep() || this.isResubmitObserve()) return void this.reusedDoc.set(null);
    this.lookupTimer = setTimeout(() => this.lookupDocByGloss(query), 400);
  }

  private lookupDocByGloss(query: string): void {
    this.isLookingUpDoc.set(true);
    const entityCode = this.request()?.entityCode ?? null;
    this.requestService.findCommissionVoto(query, entityCode).subscribe({
      next: (found) => {
        if (this.gloss().trim().toLowerCase() !== query.toLowerCase()) {
          this.isLookingUpDoc.set(false);
          return;
        }
        const doc = found?.found && found.document?.uuid ? found.document : null;
        this.reusedDoc.set(doc);
        this.isLookingUpDoc.set(false);
        if (doc?.uuid && !this.selectedFile()) {
          this.setPreviewFromUuid(doc.uuid, doc.fileName);
        } else if (!doc && !this.selectedFile() && !this.hasDocInDb()) {
          this.clearPreview();
        }
      },
      error: () => {
        this.isLookingUpDoc.set(false);
        this.reusedDoc.set(null);
      }
    });
  }

  private setPreviewFromUuid(uuid: string, name?: string): void {
    const url = `${Global.url}V1/documents/view/${uuid}`;
    const isPdf = /\.pdf$/i.test(name ?? '');
    this.revokeLocalBlob();
    if (isPdf) {
      this.previewImgUrl.set(null);
      this.previewPdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
      this.previewKind.set('pdf');
      return;
    }
    this.previewPdfUrl.set(null);
    this.previewImgUrl.set(this.sanitizer.bypassSecurityTrustUrl(url));
    this.previewKind.set('image');
  }

  private clearPreview(): void {
    this.previewImgUrl.set(null);
    this.previewPdfUrl.set(null);
    this.previewKind.set('none');
  }

  private setLocalPreview(file: File): void {
    this.revokeLocalBlob();
    this.localBlobUrl = URL.createObjectURL(file);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isPdf) {
      this.previewImgUrl.set(null);
      this.previewPdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.localBlobUrl));
      this.previewKind.set('pdf');
      return;
    }
    this.previewPdfUrl.set(null);
    this.previewImgUrl.set(this.sanitizer.bypassSecurityTrustUrl(this.localBlobUrl));
    this.previewKind.set('image');
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.isOptimizingFile.set(true);
    try {
      const optimized = file.type.startsWith('image/')
        ? ((await this.imageOptimizer.optimize(file)) || file)
        : file;
      this.selectedFile.set(optimized);
      this.fileName.set(optimized.name);
      this.setLocalPreview(optimized);
    } finally {
      this.isOptimizingFile.set(false);
      input.value = '';
    }
  }

  private clearFile(): void {
    this.revokeLocalBlob();
    this.selectedFile.set(null);
    this.fileName.set('');
    this.clearPreview();
    this.reusedDoc.set(null);
    this.isOptimizingFile.set(false);
  }

  private revokeLocalBlob(): void {
    if (this.localBlobUrl) {
      URL.revokeObjectURL(this.localBlobUrl);
      this.localBlobUrl = null;
    }
  }

  canViewPreview(): boolean {
    if (this.previewKind() === 'image') return !!this.previewImgUrl();
    if (this.previewKind() === 'pdf') return !!this.previewPdfUrl();
    return false;
  }

  viewDocument(): void {
    const doc = this.existingDocInDb();
    const uuid = doc?.document?.uuid ?? this.reusedDoc()?.uuid;
    if (uuid) window.open(`${Global.url}V1/documents/view/${uuid}`, '_blank');
  }

  confirm(): void {
    if (this.isProcessing() || this.isOptimizingFile() || this.blockedReason()) return;
    if (this.isResubmitObserve() && !this.selectedFile()) return;
    const docUuid = this.selectedFile()
      ? undefined
      : (this.reusedDoc()?.uuid ?? this.existingDocInDb()?.document?.uuid);
    const payload: StepApprovalPayload = {
      gloss: this.effectiveGloss(),
      file: this.selectedFile() ?? undefined,
      documentUuid: docUuid,
      documentTypeName: this.documentTypeName(),
      documentTypeSlug: this.stepDocSlug(),
      requiresDocument: this.requiresDocument(),
      approvedDeductible: this.isCommissionStep() ? (this.approvedDeductible() ?? undefined) : undefined,
      approvedAmount: this.isCommissionStep() ? (this.approvedAmount() ?? undefined) : undefined,
      invoicedAmount: this.isAwaitingInvoiceStep() ? (this.invoicedAmount() ?? undefined) : undefined,
      payDetails: (this.isPaymentStep() || this.isResubmitObserve())
        ? {
            txNumber: this.effectiveGloss(),
            payDate: this.payDate(),
            ...(this.isReimbursement() ? { target: this.payTarget() } : {})
          }
        : undefined
    };
    this.confirmed.emit(payload);
  }
}
